import React, { useState } from "react";
import { MapPin, Clock, Phone, Mail, Plane } from "lucide-react";
import { Link } from "react-router-dom";

const BANNER_IMG = "./img/breadcrumb-bg.jpg";

const contactInfo = [
  {
    icon: MapPin,
    title: "Office Location",
    lines: ["# 320, Neta Ji Park, Balloke Road, Badi Haibowal, Ludhiana-141001, Punjab"],
    cta: "Find Us On Map",
    href: "https://maps.google.com",
  },
  {
    icon: Clock,
    title: "Office Hour",
    lines: ["Monday-Saturday", "10.00 AM -08.00 PM"],
    cta: "Get Direction",
    href: "https://maps.google.com",
  },
  {
    icon: Phone,
    title: "Phone Number",
    lines: ["+91 9888004386"],
    cta: "Call Now",
    href: "tel:+919888004386",
  },
  {
    icon: Mail,
    title: "E-mail Address",
    lines: ["reliable.express@outlook.com"],
    cta: "Mail Us",
    href: "mailto:reliable.express@outlook.com",
  },
];

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  // { type: "success" | "error", text: "..." } or null
  const [formStatus, setFormStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setFormStatus(null);

    try {
      const response = await fetch("/api/contact.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      // Server may return non-JSON (e.g. a 404 page), so don't let that crash
      let data = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (response.ok && data.success) {
        setFormStatus({
          type: "success",
          text: "Thank you! Your message has been sent successfully. Our team will contact you soon.",
        });

        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
      } else {
        setFormStatus({
          type: "error",
          text: data.message || "Something went wrong. Please try again.",
        });
      }
    } catch (error) {
      console.error("Contact form error:", error);
      setFormStatus({
        type: "error",
        text: "Unable to send your message. Please check your connection and try again.",
      });
    }

    setLoading(false);

    // Hide the message after 6 seconds
    setTimeout(() => setFormStatus(null), 6000);
  };

  return (
    <>
      <section
        className="rx-page-banner"
        style={{ backgroundImage: `url(${BANNER_IMG})` }}
      >
        <div className="rx-page-banner-overlay" />

        <div className="rx-page-banner-inner">
          <h1>Contact Us</h1>

          <div className="rx-breadcrumb">
            <Link to="/">Home</Link>
            <span>»</span>
            <span className="current">Contact Us</span>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="rx-contact-info-section">
        <div className="rx-section-head">
          <div className="rx-section-eyebrow" style={{ textAlign: "center" }}>
            Contact Us
          </div>

          <h2
            style={{
              fontFamily: "var(--font-display)",
              color: "var(--ink)",
            }}
          >
            Don't hesitate to contact for get Information
          </h2>
        </div>

        <div className="rx-contact-info-grid">
          {contactInfo.map((item, i) => {
            const Icon = item.icon;

            return (
              <div
                key={item.title}
                className="rx-contact-info-item"
                style={{
                  borderRight:
                    i !== contactInfo.length - 1
                      ? "1px solid var(--hairline)"
                      : "none",
                }}
              >
                <div className="rx-contact-icon-circle">
                  <Icon size={22} />
                </div>

                <h5>{item.title}</h5>

                {item.lines.map((line, li) => (
                  <p key={li}>{line}</p>
                ))}

                <a href={item.href} className="rx-contact-cta">
                  {item.cta}
                </a>
              </div>
            );
          })}
        </div>
      </section>

      {/* Get in Touch */}
      <section className="rx-touch-section">
        <Plane size={40} className="rx-touch-plane rx-touch-plane-1" />
        <Plane size={30} className="rx-touch-plane rx-touch-plane-2" />

        <div className="rx-touch-inner">
          <div className="rx-touch-kicker">Stay with Us</div>

          <h2 className="rx-touch-title">
            Get in <span>Touch</span>
          </h2>

          <form className="rx-contact-form" onSubmit={handleSubmit}>
            <div className="rx-form-row">
              <input
                type="text"
                name="name"
                className="rx-form-input"
                placeholder="Your Name*"
                value={formData.name}
                onChange={handleChange}
                required
              />

              <input
                type="email"
                name="email"
                className="rx-form-input"
                placeholder="Your Email*"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="rx-form-row">
              <input
                type="tel"
                name="phone"
                className="rx-form-input"
                placeholder="Contact No.*"
                value={formData.phone}
                onChange={handleChange}
                required
              />

              <select
                name="subject"
                className="rx-form-input"
                value={formData.subject}
                onChange={handleChange}
                required
              >
                <option value="" disabled>
                  Subject
                </option>
                <option value="Logistics Solutions">Logistics Solutions</option>
                <option value="E-Commerce Logistics">E-Commerce Logistics</option>
                <option value="Warehousing Solutions">Warehousing Solutions</option>
                <option value="Government Project Deliveries">
                  Government Project Deliveries
                </option>
                <option value="General Inquiry">General Inquiry</option>
              </select>
            </div>

            <textarea
              name="message"
              className="rx-form-textarea"
              rows="5"
              placeholder="Message"
              value={formData.message}
              onChange={handleChange}
            />

            <div style={{ textAlign: "center" }}>
              <button
                type="submit"
                className="rx-form-submit"
                disabled={loading}
              >
                {loading ? "Sending..." : "Submit"}
              </button>
            </div>

            {formStatus && (
              <div
                role="status"
                style={{
                  marginTop: "20px",
                  padding: "14px 18px",
                  borderRadius: "8px",
                  textAlign: "center",
                  fontWeight: 600,
                  color: formStatus.type === "success" ? "#0f5132" : "#842029",
                  background: formStatus.type === "success" ? "#d1e7dd" : "#f8d7da",
                  border: `1px solid ${
                    formStatus.type === "success" ? "#badbcc" : "#f5c2c7"
                  }`,
                }}
              >
                {formStatus.type === "success" ? "✓ " : "⚠ "}
                {formStatus.text}
              </div>
            )}
          </form>
        </div>
      </section>

      <iframe
        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d14025.15146687171!2d77.4038711557737!3d28.500985611785843!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce85774d73ba7%3A0x22f7a6469d4b3883!2sSector%20142%2C%20Noida%2C%20Uttar%20Pradesh%20201304!5e0!3m2!1sen!2sin!4v1787133356091!5m2!1sen!2sin"
        width="100%"
        height="450"
        style={{ border: 0, display: "block" }}
        allowFullScreen
        loading="lazy"
        referrerPolicy="strict-origin-when-cross-origin"
        title="Reliable Express Office Location"
      />
    </>
  );
}